// ============================================================================
// ChatMembersBlock — блок «Участники» правой панели чата: аватар, имя, роли
// (entity.listEntityRoles), пресенс (member_last_seen: «онлайн» ≤2 мин /
// «был(а) …») и действия: «Написать» (личный диалог), «Добавить участника»
// (групповой чат — любой участник), «Удалить из группы» (создатель чата или
// администратор — правило сервера; создателя удалить нельзя, у себя — не
// показываем). Права/событие/уведомление об изменении состава — СЕРВЕР.
// ============================================================================

import { useEffect, useState } from 'react';
import { MessageSquare, UserMinus, UserPlus } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { friendlyError, logSwallowed } from '@/lib/errors';
import { confirmDialog } from '@/lib/dialogs';
import { createLogger } from '@/lib/logger';
import { useToastStore } from '@/store/toastStore';
import { lastSeenLabel } from './chatThreadParts';
import { loadMemberRoles } from './chatInfoData';
import type { ThreadUser } from './ChatThread';

const log = createLogger('ChatMembersBlock');

interface ChatMembersBlockProps {
  memberIds: string[];
  users: ThreadUser[];
  myId: string;
  memberLastSeen: Record<string, string>;
  onWrite: (memberId: string) => Promise<void>;
  /** Создатель чата (бейдж «Создатель»; удалить нельзя — правило сервера). */
  createdBy?: string;
  /** Право управления составом (создатель чата или глобальный администратор). */
  canManage?: boolean;
  /** Открыть диалог добавления участника (показывается у группового чата). */
  onAddMember?: () => void;
  /** Удалить участника из группы (права и событие — сервер). */
  onRemoveMember?: (memberId: string) => Promise<void>;
}

export function ChatMembersBlock({
  memberIds, users, myId, memberLastSeen, onWrite,
  createdBy = '', canManage = false, onAddMember, onRemoveMember,
}: ChatMembersBlockProps) {
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  const [busyId, setBusyId] = useState('');
  const [removingId, setRemovingId] = useState('');

  useEffect(() => {
    let alive = true;
    if (memberIds.length === 0) return;
    loadMemberRoles(memberIds)
      .then((map) => { if (alive) setRoles(map); })
      .catch((e) => {
        logSwallowed(e, 'components/chat/ChatMembersBlock.tsx:roles');
        if (alive) setRoles({});
      });
    return () => { alive = false; };
  }, [memberIds]);

  if (memberIds.length === 0) {
    return (
      <div className="glass-panel rounded-xl p-3 text-[11px] text-muted-foreground">
        Участники не загружены — проверьте связь с сервером и обновите панель.
      </div>
    );
  }

  const nameOf = (id: string) => users.find((u) => u.id === id)?.name || id;
  const avatarOf = (id: string) => users.find((u) => u.id === id)?.avatarUrl;

  const handleRemove = async (id: string) => {
    if (!onRemoveMember) return;
    const ok = await confirmDialog(`Удалить «${nameOf(id)}» из группы?`);
    if (!ok) return;
    setRemovingId(id);
    try {
      await onRemoveMember(id);
    } catch (e) {
      log.error('не удалось удалить участника', e);
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div className="glass-panel rounded-xl p-2 space-y-1">
      {/* Состав группы: «Добавить» — участникам группового чата (любой
          участник; правило ядра), удаление — создателю/админу */}
      {(onAddMember || canManage) && (
        <div className="flex items-center gap-2 px-1.5 pb-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Участники · {memberIds.length}
          </span>
          {onAddMember && (
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-primary/30 px-2 py-0.5 text-[10px] text-primary hover:bg-primary/10"
              title="Добавить участника в группу"
              onClick={onAddMember}
            >
              <UserPlus className="h-3 w-3" />
              Добавить
            </button>
          )}
        </div>
      )}
      {memberIds.map((id) => {
        const isMe = id === myId;
        const isCreator = !!createdBy && id === createdBy;
        const presence = lastSeenLabel(memberLastSeen[id] || '');
        const memberRoles = roles[id] || [];
        const canRemoveThis = canManage && !isMe && !isCreator;
        return (
          <div key={id} className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 hover:bg-accent/30">
            <UserAvatar uid={id} fileId={avatarOf(id)} name={nameOf(id)} size={30} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="truncate text-[12px] font-medium">{nameOf(id)}</span>
                {isMe && <span className="shrink-0 rounded bg-muted px-1 text-[9px] text-muted-foreground">Вы</span>}
                {isCreator && <span className="shrink-0 rounded bg-primary/10 text-primary px-1 text-[9px]">Создатель</span>}
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                {memberRoles.slice(0, 3).map((r) => (
                  <span key={r} className="shrink-0 rounded bg-primary/10 text-primary px-1 py-px text-[9px] truncate max-w-[72px]" title={r}>
                    {r}
                  </span>
                ))}
                <span className={`truncate text-[10px] ${presence === 'онлайн' ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                  {presence || 'не в сети'}
                </span>
              </div>
            </div>
            {!isMe && (
              <button
                type="button"
                className="shrink-0 p-1.5 rounded-full hover:bg-muted/60 text-muted-foreground disabled:opacity-40"
                title="Написать в личный чат"
                aria-label={`Написать ${nameOf(id)}`}
                disabled={busyId === id || removingId === id}
                onClick={() => {
                  setBusyId(id);
                  void onWrite(id).finally(() => setBusyId(''));
                }}
              >
                <MessageSquare className="h-3.5 w-3.5" />
              </button>
            )}
            {canRemoveThis && (
              <button
                type="button"
                className="shrink-0 p-1.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-40"
                title="Удалить из группы"
                aria-label={`Удалить ${nameOf(id)} из группы`}
                disabled={removingId === id}
                onClick={() => void handleRemove(id)}
              >
                <UserMinus className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
